import { Check } from 'lucide-react'
import type { Quest } from '@movmania/core'
import type { TFunction } from '../lib/useT.js'
import { useT } from '../lib/useT.js'
import { Card } from './Ui.js'

/** Libellé lisible d'une quête, en un seul endroit. */
export function questLabel(t: TFunction, quest: Quest): string {
  return t(`quest.${quest.kind}`, {
    count: quest.target,
    activity: quest.activityType ? t(`activityType.${quest.activityType}`) : '',
  })
}

export function QuestList({
  quests,
  title,
}: {
  quests: readonly Quest[]
  title: string
}) {
  const t = useT()
  if (quests.length === 0) return null

  return (
    <Card>
      <h3 className="text-caption text-ink-3 mb-3">{title}</h3>
      <ul className="space-y-3">
        {quests.map((quest) => {
          const label = questLabel(t, quest)
          const progress = Math.min(1, quest.progress / quest.target)
          return (
            <li key={quest.id} className="flex items-center gap-3">
              <span
                className={`grid size-7 shrink-0 place-items-center rounded-full ${
                  quest.done ? 'bg-green text-surface' : 'bg-sunken'
                }`}
                aria-hidden="true"
              >
                {quest.done && <Check size={15} strokeWidth={3} />}
              </span>

              <span
                className={`text-body min-w-0 flex-1 ${quest.done ? 'text-ink-3' : 'text-ink'}`}
              >
                {label}
              </span>

              {/* Avancement visuel, sans compteur : la longueur se lit d'un
                  coup, « 2/3 » fait faire un calcul. */}
              {!quest.done && (
                <span
                  className="bg-sunken h-2 w-12 shrink-0 overflow-hidden rounded-full"
                  role="progressbar"
                  aria-label={label}
                  aria-valuenow={Math.round(progress * 100)}
                  aria-valuemin={0}
                  aria-valuemax={100}
                >
                  <span
                    className="bg-accent block h-full rounded-full"
                    style={{ width: `${progress * 100}%` }}
                  />
                </span>
              )}
            </li>
          )
        })}
      </ul>
    </Card>
  )
}
